import * as THREE from 'three';
import { CubeMesh } from './cube';
import type { SceneAssets } from './assets';
import type { TrayPiece } from '../core/engine';
import { CELL } from './board';
import { tween, smoothstep } from './anim';

export interface PieceGroup {
  group: THREE.Group;
  cubes: CubeMesh[];
}

/** Build a group of numbered cubes for a piece.
 *  centered=true: piece centered on its bounding box (for the tray display).
 *  centered=false: cell (0,0) sits at the group origin (for the drag ghost anchored to a board cell). */
export function buildPiece(
  cells: { x: number; z: number }[],
  value: number,
  assets: SceneAssets,
  centered: boolean,
): PieceGroup {
  const group = new THREE.Group();
  const cubes: CubeMesh[] = [];
  let ox = 0;
  let oz = 0;
  if (centered) {
    const maxX = Math.max(...cells.map((c) => c.x));
    const maxZ = Math.max(...cells.map((c) => c.z));
    ox = maxX / 2;
    oz = maxZ / 2;
  }
  for (const cell of cells) {
    const cube = new CubeMesh(value, assets);
    cube.group.position.set((cell.x - ox) * CELL, 0, (cell.z - oz) * CELL);
    group.add(cube.group);
    cubes.push(cube);
  }
  return { group, cubes };
}

class PieceView {
  pieceGroup: PieceGroup;
  constructor(
    public slot: number,
    piece: TrayPiece,
    slotPos: THREE.Vector3,
    scale: number,
    assets: SceneAssets,
  ) {
    this.pieceGroup = buildPiece(piece.cells, piece.value, assets, true);
    this.pieceGroup.group.position.copy(slotPos);
    this.pieceGroup.group.scale.setScalar(scale);
  }
  dispose(): void {
    for (const c of this.pieceGroup.cubes) c.dispose();
    this.pieceGroup.group.removeFromParent();
  }
}

/** The drawer: shows the 3 pieces waiting to be placed. */
export class TrayView {
  group = new THREE.Group();
  private slots: (PieceView | null)[] = [null, null, null];
  private assets: SceneAssets;
  readonly scale = 0.5;
  readonly slotX = [-3.4, 0, 3.4];
  readonly slotY = -0.85; // below the board surface, so the drawer slides UNDER the board when closed
  readonly slotZ = 6.9;
  private readonly closedOffset = -5; // how far back (in Z) the drawer tucks into the box

  constructor(assets: SceneAssets) {
    this.assets = assets;
    this.buildDrawer();
    this.group.position.z = this.closedOffset; // start tucked in (closed)
  }

  /** The wooden drawer the pieces rest in. */
  private buildDrawer(): void {
    const width = 11.4;
    const depth = 3.6;
    const z = this.slotZ;
    const wood = new THREE.MeshStandardMaterial({
      map: this.assets.boardMap ?? null, // Board_Base_Color — the original box/drawer wood
      color: this.assets.boardMap ? 0xbcab88 : 0x4a3520,
      roughness: 0.8,
      metalness: 0.04,
    });
    const floor = new THREE.Mesh(new THREE.BoxGeometry(width, 0.3, depth), wood);
    floor.position.set(0, this.slotY - 0.39, z);
    floor.receiveShadow = true;
    floor.castShadow = true;
    this.group.add(floor);

    const rimH = 0.5;
    const t = 0.4;
    const halfW = width / 2;
    const halfD = depth / 2;
    const segs: [number, number, number, number][] = [
      [0, z + halfD - t / 2, width, t],
      [0, z - halfD + t / 2, width, t],
      [halfW - t / 2, z, t, depth],
      [-(halfW - t / 2), z, t, depth],
    ];
    for (const [cx, cz, w, d] of segs) {
      const bar = new THREE.Mesh(new THREE.BoxGeometry(w, rimH, d), wood);
      bar.position.set(cx, this.slotY - 0.19, cz);
      bar.castShadow = true;
      bar.receiveShadow = true;
      this.group.add(bar);
    }
  }

  slotWorld(slot: number): THREE.Vector3 {
    return new THREE.Vector3(this.slotX[slot], this.slotY, this.slotZ);
  }

  /** Slide the drawer OUT toward the viewer to present the pieces (Unity Tray.anim, +Z, 0.833s). */
  slideOpen(): void {
    const from = this.group.position.z;
    tween(833, (t) => (this.group.position.z = from + (0 - from) * t), { ease: smoothstep });
  }

  /** Slide the drawer back INTO the box (the restock close beat). */
  slideClosed(): void {
    const from = this.group.position.z;
    tween(833, (t) => (this.group.position.z = from + (this.closedOffset - from) * t), {
      ease: smoothstep,
    });
  }

  setTray(tray: (TrayPiece | null)[]): void {
    for (let i = 0; i < 3; i++) this.rebuildSlot(i, tray[i]);
  }

  rebuildSlot(slot: number, piece: TrayPiece | null): void {
    this.slots[slot]?.dispose();
    if (piece) {
      const view = new PieceView(slot, piece, this.slotWorld(slot), this.scale, this.assets);
      view.pieceGroup.group.userData.slot = slot;
      this.group.add(view.pieceGroup.group);
      this.slots[slot] = view;
    } else {
      this.slots[slot] = null;
    }
  }

  setSlotVisible(slot: number, visible: boolean): void {
    const v = this.slots[slot];
    if (v) v.pieceGroup.group.visible = visible;
  }

  hasPiece(slot: number): boolean {
    return this.slots[slot] !== null;
  }

  /** Meshes used for picking a piece (pointer raycast). */
  pickMeshes(): { slot: number; object: THREE.Object3D }[] {
    const out: { slot: number; object: THREE.Object3D }[] = [];
    this.slots.forEach((v, slot) => {
      if (v) out.push({ slot, object: v.pieceGroup.group });
    });
    return out;
  }

  pieceWorldCenter(slot: number): THREE.Vector3 | null {
    const v = this.slots[slot];
    if (!v) return null;
    return v.pieceGroup.group.getWorldPosition(new THREE.Vector3());
  }

  clear(): void {
    for (let i = 0; i < 3; i++) {
      this.slots[i]?.dispose();
      this.slots[i] = null;
    }
  }
}
